import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Product Installments Custom Element
 * 
 * @description A custom element that displays product installment payment options
 * and automatically updates when product variants change. This component reads the
 * budget_pay.installments metafield from variants and displays Flexi Pay and other
 * installment options. It handles cases where installments may appear, disappear, or
 * change based on the selected variant.
 * 
 * @class ProductInstallments
 * @extends HTMLElement
 * 
 * @example
 * <product-installments data-product-id="123456">
 *   <div class="product-installments">
 *     <div class="product-installments__item">
 *       <div class="product-installments__logo">...</div>
 *       <div class="product-installments__content">5 x £280 interest-free payments.</div>
 *     </div>
 *   </div>
 * </product-installments>
 * 
 * @dependencies
 * - @theme/events - ThemeEvents and VariantUpdateEvent types
 * - snippets/product-installments.liquid - Liquid template that renders installment HTML
 * 
 * @metafields
 * - variant.metafields.budget_pay.installments.value (integer) - Number of installments available
 * 
 * @event-listens ThemeEvents.variantUpdate
 * Fired when a variant is selected, contains updated HTML in event.detail.data.html
 */
class ProductInstallments extends HTMLElement {
  /**
   * Called when the element is inserted into the DOM.
   * Sets up event listener for variant updates.
   * 
   * @returns {void}
   */
  connectedCallback() {
    // Find the closest section or dialog to attach event listener
    // This ensures we only listen to events within the same product context
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    // Listen for variant update events from the variant picker
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
    // Open installments drawer when "Show more" / "Learn more" is clicked
    this.addEventListener('click', this.#handleLinkClick);
  }

  /**
   * Called when the element is removed from the DOM.
   * Cleans up event listener to prevent memory leaks.
   * 
   * @returns {void}
   */
  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    
    // Remove event listener to prevent memory leaks
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateInstallments);
    this.removeEventListener('click', this.#handleLinkClick);
  }

  /**
   * Delegated click: open installments drawer when link has data-installments-drawer.
   * @param {MouseEvent} event
   */
  #handleLinkClick = (event) => {
    const link = event.target.closest('.product-installments__link');
    if (!link || !(link instanceof HTMLElement)) return;
    const provider = link.dataset.installmentsDrawer;
    if (provider) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('installments-drawer-open', { bubbles: true, detail: { provider } }));
    }
  };

  /**
   * Updates the installments section with new variant data.
   * 
   * This method handles three scenarios:
   * 1. Installments exist in new HTML and current HTML - Replace if changed
   * 2. Installments exist in new HTML but not current - Add them
   * 3. Installments don't exist in new HTML but exist in current - Remove them
   * 
   * This ensures the UI accurately reflects whether the selected variant
   * supports installment payments based on its metafield data.
   * 
   * @param {VariantUpdateEvent} event - The variant update event containing new HTML
   * @property {Object} event.detail.data - Event data object
   * @property {Object} event.detail.data.newProduct - New product object (if product changed)
   * @property {DocumentFragment} event.detail.data.html - Updated HTML fragment
   * 
   * @returns {void}
   */
  updateInstallments = (event) => {
    // If this is a new product, update the product ID
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } 
    // If event is for a different product, ignore it
    else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new installments HTML in the updated fragment
    const newInstallments = event.detail.data.html.querySelector('.product-installments');
    // Find the current installments element in this component
    const currentInstallments = this.querySelector('.product-installments');
    
    // Scenario 1 & 2: New installments exist
    if (newInstallments) {
      if (currentInstallments) {
        // Scenario 1: Replace existing installments if content changed
        if (currentInstallments.innerHTML !== newInstallments.innerHTML) {
          currentInstallments.replaceWith(newInstallments.cloneNode(true));
        }
      } else {
        // Scenario 2: Add new installments if they don't currently exist
        this.appendChild(newInstallments.cloneNode(true));
      }
    } 
    // Scenario 3: Remove installments if they don't exist for new variant
    else if (currentInstallments) {
      currentInstallments.remove();
    }
  };
}

if (!customElements.get('product-installments')) {
  customElements.define('product-installments', ProductInstallments);
}
